import React from 'react';
import { X, ChevronRight } from 'lucide-react';
import GuestRecorderCard from '../recorder/GuestRecorderCard';

interface PathSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  modalState: 'closed' | 'selection' | 'recorder';
  onSelectPresencial: () => void;
  onSelectRemote: () => void;
  onBackToSelection: () => void;
  onSaveRecording: () => void;
  question: string;
}

const PathSelectionModal: React.FC<PathSelectionModalProps> = ({
  isOpen,
  onClose,
  modalState,
  onSelectPresencial,
  onSelectRemote,
  onBackToSelection,
  onSaveRecording,
  question
}) => {
  if (!isOpen || modalState === 'closed') return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-rose-50/95 via-stone-50/95 to-amber-50/95 backdrop-blur-md flex items-center justify-center z-success p-4 animate-fade-in overflow-y-auto py-8 md:py-4">
      {/* Decorative blobs */}
      <div className="absolute top-10 right-10 w-72 h-72 bg-rose-200/30 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className={`flex flex-col w-full ${modalState === 'selection' ? 'max-w-sm md:max-w-3xl' : 'max-w-md'} relative z-10`}>
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 md:-right-12 text-stone-600 hover:text-stone-900 bg-white/80 hover:bg-white rounded-full p-2 transition shadow-lg z-20"
          aria-label="Cerrar modal"
        >
          <X size={28} />
        </button>

        {modalState === 'selection' && (
          <div className="animate-fade-in">
            <div className="text-center mb-6 md:mb-8">
              <h2 className="text-2xl md:text-3xl font-serif font-bold text-stone-900 mb-2">Comienza el viaje</h2>
              <p className="text-stone-600 text-base max-w-md mx-auto">
                Elige cómo quieres capturar este momento único hoy.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div
                onClick={onSelectPresencial}
                className="bg-white rounded-2xl overflow-hidden shadow-2xl cursor-pointer hover:scale-[1.02] transition-transform active:scale-95 group flex flex-row md:flex-col h-32 md:h-auto"
              >
                <div className="w-[40%] md:w-full h-full md:h-48 shrink-0 relative overflow-hidden">
                  <div className="absolute inset-0 bg-stone-900/10 group-hover:bg-transparent transition-colors z-10"></div>
                  <img
                    src="/unnamed_6.jpg"
                    alt="Familia grabando junta"
                    className="w-full h-full object-cover absolute inset-0 group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="w-[60%] md:w-full p-5 flex flex-col justify-center relative">
                  <h3 className="text-lg font-bold text-stone-900 mb-1 leading-tight group-hover:text-rose-600 transition-colors">
                    Estoy con mi familiar
                  </h3>
                  <p className="text-xs text-stone-500 leading-relaxed">
                    Quiero empezar con los temas ya.
                  </p>
                  <div className="hidden md:flex absolute top-5 right-5 w-8 h-8 rounded-full bg-stone-100 items-center justify-center text-stone-400 group-hover:bg-rose-100 group-hover:text-rose-500 transition-colors">
                    <ChevronRight size={16} />
                  </div>
                </div>
              </div>

              <div
                onClick={onSelectRemote}
                className="bg-white rounded-2xl overflow-hidden shadow-2xl cursor-pointer hover:scale-[1.02] transition-transform active:scale-95 group flex flex-row md:flex-col h-32 md:h-auto"
              >
                <div className="w-[40%] md:w-full h-full md:h-48 shrink-0 relative overflow-hidden">
                  <div className="absolute inset-0 bg-stone-900/10 group-hover:bg-transparent transition-colors z-10"></div>
                  <img
                    src="/unnamed_7.jpg"
                    alt="Persona conectada a distancia"
                    className="w-full h-full object-cover absolute inset-0 group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="w-[60%] md:w-full p-5 flex flex-col justify-center relative">
                  <h3 className="text-lg font-bold text-stone-900 mb-1 leading-tight group-hover:text-rose-600 transition-colors">
                    A distancia
                  </h3>
                  <p className="text-xs text-stone-500 leading-relaxed">
  No estamos juntos ahora :(<br/>
  Quiero que pueda empezar a responder a distancia.
                  </p>
                  <div className="hidden md:flex absolute top-5 right-5 w-8 h-8 rounded-full bg-stone-100 items-center justify-center text-stone-400 group-hover:bg-rose-100 group-hover:text-rose-500 transition-colors">
                    <ChevronRight size={16} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {modalState === 'recorder' && (
          <div className="animate-slide-up w-full">
            <button
              onClick={onBackToSelection}
              className="text-stone-600 text-sm mb-6 flex items-center gap-2 hover:text-stone-900 transition-colors pl-1 bg-white/60 hover:bg-white/80 px-3 py-2 rounded-lg"
            >
              <ChevronRight size={16} className="rotate-180" />
              Volver a elegir
            </button>
            <GuestRecorderCard
              question={question}
              onClose={onClose}
              onSaveClick={onSaveRecording}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PathSelectionModal;
